import React, { useEffect, useState, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageContext } from '../../App';
import { createPortal } from 'react-dom';
interface TooltipProps {
  content: string;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delay?: number;
  className?: string;
}
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  align = 'center',
  delay = 400,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const {
    direction
  } = useContext(LanguageContext);
  // Adjust side based on RTL direction
  const adjustedSide = direction === 'rtl' && (side === 'left' || side === 'right') ? side === 'left' ? 'right' : 'left' : side;
  // Handle showing tooltip with delay
  const handleMouseEnter = () => {
    setIsMounted(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };
  // Handle hiding tooltip
  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
    setTimeout(() => {
      if (!isVisible) {
        setIsMounted(false);
      }
    }, 200);
  };
  // Calculate position
  useEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return;
    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();
      if (!triggerRect || !tooltipRect) return;
      // Calculate base position
      let x = 0;
      let y = 0;
      // Position based on side
      switch (adjustedSide) {
        case 'top':
          y = -tooltipRect.height - 8;
          break;
        case 'right':
          x = triggerRect.width + 8;
          break;
        case 'bottom':
          y = triggerRect.height + 8;
          break;
        case 'left':
          x = -tooltipRect.width - 8;
          break;
      }
      // Adjust alignment
      if (adjustedSide === 'top' || adjustedSide === 'bottom') {
        switch (align) {
          case 'start':
            x = 0;
            break;
          case 'center':
            x = (triggerRect.width - tooltipRect.width) / 2;
            break;
          case 'end':
            x = triggerRect.width - tooltipRect.width;
            break;
        }
      } else if (adjustedSide === 'left' || adjustedSide === 'right') {
        switch (align) {
          case 'start':
            y = 0;
            break;
          case 'center':
            y = (triggerRect.height - tooltipRect.height) / 2;
            break;
          case 'end':
            y = triggerRect.height - tooltipRect.height;
            break;
        }
      }
      if (tooltipRef.current) {
        tooltipRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isVisible, adjustedSide, align]);
  return <div className="inline-block relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onFocus={handleMouseEnter} onBlur={handleMouseLeave} ref={triggerRef} data-id="element-776">
      {children}
      {isMounted && createPortal(<div className="fixed z-[1000]" ref={tooltipRef} style={{
      top: triggerRef.current?.getBoundingClientRect().top || 0,
      left: triggerRef.current?.getBoundingClientRect().left || 0,
      pointerEvents: 'none'
    }} data-id="element-777">
            <AnimatePresence data-id="element-778">
              {isVisible && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.15
        }} className={`px-2 py-1 text-xs font-medium bg-gray-800 text-white rounded-md shadow-sm whitespace-nowrap ${className}`} data-id="element-779">
                  {content}
                </motion.div>}
            </AnimatePresence>
          </div>, document.body)}
    </div>;
};